import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon, type IconKind } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { ErrorNote, Field, FormActions, FormGroup, FormPanel, OkNote, Select, errorMessage, shortDate } from '@/components/ui';
import { formatNumber } from '@/format';
import { hrGet, useHrInvalidation } from '@/hooks/hr';
import { useAuth } from '@/stores/auth';

export type MasterKind = 'departments' | 'designations' | 'shifts' | 'pay-components' | 'leave-types';

type PayKind = 'EARNING' | 'DEDUCTION' | 'EMPLOYER';

interface MasterRow {
  id: string;
  code: string;
  nameEn: string;
  nameUr: string | null;
  isActive: boolean;
  updatedAt: string;
  inUse: number;
  // Shifts
  startTime?: string;
  endTime?: string;
  isNight?: boolean;
  // Pay items
  kind?: PayKind;
  systemCode?: string | null;
  taxable?: boolean;
  countsForOvertime?: boolean;
  percentOfBasic?: string | null;
  sortOrder?: number;
  // Leave types
  isPaid?: boolean;
  yearlyDays?: string;
  carryForwardMax?: string;
  encashable?: boolean;
  forDailyWorkers?: boolean;
}

const KINDS: Record<
  MasterKind,
  { one: string; many: string; icon: IconKind; entity: string; intro: string; example: string; usedBy: string }
> = {
  departments: {
    one: 'department',
    many: 'Departments',
    icon: 'department',
    entity: 'Department',
    intro: 'Groups of staff, such as Farm operations or Accounts.',
    example: 'Farm operations',
    usedBy: 'People',
  },
  designations: {
    one: 'designation',
    many: 'Designations',
    icon: 'designation',
    entity: 'Designation',
    intro: 'Job titles, such as Supervisor, Shed worker or Driver.',
    example: 'Shed worker',
    usedBy: 'People',
  },
  shifts: {
    one: 'shift',
    many: 'Shifts',
    icon: 'shift',
    entity: 'Shift',
    intro: 'Working hours. Optional; only needed if staff work different shifts.',
    example: 'Day shift',
    usedBy: 'Used by',
  },
  'pay-components': {
    one: 'pay item',
    many: 'Pay items',
    icon: 'payroll',
    entity: 'PayComponent',
    intro: 'What makes up pay: added to pay (earnings), taken off (deductions), or paid by the company on top (company cost).',
    example: 'House rent',
    usedBy: 'Used by',
  },
  'leave-types': {
    one: 'leave type',
    many: 'Leave types',
    icon: 'leave',
    entity: 'LeaveType',
    intro: 'Kinds of leave and how many days a year each person gets.',
    example: 'Casual leave',
    usedBy: 'Used by',
  },
};

const PAY_KIND_TEXT: Record<PayKind, string> = {
  EARNING: 'Earning',
  DEDUCTION: 'Deduction',
  EMPLOYER: 'Company cost',
};

const SYSTEM_REASON = 'Worked out by payroll; you can rename it';

function yes(v: boolean | undefined): string {
  return v ? 'Yes' : '—';
}

function days(v: string | undefined): string {
  return v === undefined ? '—' : formatNumber(v);
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <label className="settings__toggle" title={hint}>
      <input type="checkbox" checked={checked} disabled={disabled ?? false} onChange={(e) => onChange(e.target.checked)} />
      <span className="field__label">{label}</span>
    </label>
  );
}

/**
 * One HR list — departments, designations, shifts, pay items or leave types:
 * a grid with add, edit, retire and reactivate, like Items Manager. Something
 * in use is switched off rather than deleted, so old records still read
 * correctly; pay items payroll works out itself can only be renamed.
 */
export function MasterPanel({ kind }: { kind: MasterKind }) {
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const k = KINDS[kind];
  const editable = can('hr.setup.update');
  const [showOff, setShowOff] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [form, setForm] = useState<{ mode: 'create' } | { mode: 'edit'; row: MasterRow } | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'setup', kind, showOff],
    queryFn: () => hrGet<MasterRow[]>(`/api/v1/hr/setup/${kind}`, { includeInactive: showOff ? 'true' : undefined }),
  });
  const rows = list.data ?? [];
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  const retire = useMutation({
    mutationFn: (row: MasterRow) =>
      api.post<{ outcome: 'deleted' | 'deactivated'; message: string }>(`/api/v1/hr/setup/${kind}/${row.id}/retire`),
    onSuccess: (r, row) => {
      invalidate();
      if (r.outcome === 'deleted') setSelectedKey(null);
      setNotice({ tone: 'ok', message: r.message ?? `${row.code} deleted.` });
    },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not delete it') }),
  });
  const reactivate = useMutation({
    mutationFn: (row: MasterRow) => api.post(`/api/v1/hr/setup/${kind}/${row.id}/reactivate`),
    onSuccess: (_, row) => {
      invalidate();
      setNotice({ tone: 'ok', message: `${row.code} switched back on.` });
    },
    onError: (e) => setNotice({ tone: 'error', message: errorMessage(e, 'Could not switch it back on') }),
  });

  function rowActions(row: MasterRow): DesktopAction[] {
    const system = kind === 'pay-components' && !!row.systemCode;
    return [
      {
        label: 'Edit',
        icon: 'edit',
        shortcut: 'F2',
        onSelect: () => setForm({ mode: 'edit', row }),
        allowed: editable,
      },
      row.isActive
        ? {
            label: 'Delete',
            icon: 'retire',
            danger: true,
            separatorBefore: true,
            allowed: editable,
            enabled: !system,
            reason: SYSTEM_REASON,
            onSelect: () => {
              const warn =
                row.inUse > 0
                  ? `It is in use, so it will be switched off rather than deleted. Old records keep it.`
                  : 'It is not used anywhere, so it will be deleted.';
              if (window.confirm(`Delete ${row.code} ${row.nameEn}? ${warn}`)) retire.mutate(row);
            },
          }
        : {
            label: 'Reactivate',
            icon: 'reactivate',
            separatorBefore: true,
            allowed: editable,
            onSelect: () => reactivate.mutate(row),
          },
    ];
  }

  const toolbar: DesktopAction[] = [
    {
      label: `Add ${k.one}`,
      icon: 'add',
      primary: true,
      shortcut: 'Ins',
      onSelect: () => setForm({ mode: 'create' }),
      allowed: editable,
    },
    ...(selected
      ? rowActions(selected)
      : [
          {
            label: 'Edit',
            icon: 'edit' as const,
            onSelect: () => {},
            allowed: editable,
            enabled: false,
            reason: `Choose a ${k.one} first`,
            separatorBefore: true,
          },
        ]),
  ];

  const extra: Array<GridColumn<MasterRow>> =
    kind === 'shifts'
      ? [
          { key: 'time', header: 'Hours', width: 120, render: (r) => `${r.startTime ?? ''} – ${r.endTime ?? ''}` },
          { key: 'night', header: 'Night shift', width: 100, render: (r) => yes(r.isNight) },
        ]
      : kind === 'pay-components'
        ? [
            { key: 'kind', header: 'Kind', width: 120, render: (r) => (r.kind ? PAY_KIND_TEXT[r.kind] : '—') },
            { key: 'taxable', header: 'Taxable', width: 80, render: (r) => yes(r.taxable) },
            { key: 'ot', header: 'Counts for overtime', width: 150, render: (r) => yes(r.countsForOvertime) },
            { key: 'pct', header: '% of basic', align: 'end', width: 100, render: (r) => (r.percentOfBasic ? `${formatNumber(r.percentOfBasic)}%` : '—') },
            {
              key: 'system',
              header: 'Worked out by payroll',
              width: 170,
              render: (r) => (r.systemCode ? <span className="pill">Yes</span> : <span className="dim">—</span>),
            },
          ]
        : kind === 'leave-types'
          ? [
              { key: 'paid', header: 'Paid', width: 70, render: (r) => (r.isPaid ? 'Paid' : 'Unpaid') },
              { key: 'days', header: 'Days a year', align: 'end', width: 100, render: (r) => days(r.yearlyDays) },
              { key: 'carry', header: 'Carry forward', align: 'end', width: 120, render: (r) => days(r.carryForwardMax) },
              { key: 'encash', header: 'Paid out on leaving', width: 150, render: (r) => yes(r.encashable) },
              { key: 'daily', header: 'For daily-wage staff', width: 150, render: (r) => yes(r.forDailyWorkers) },
            ]
          : [];

  const columns: Array<GridColumn<MasterRow>> = [
    { key: 'code', header: 'Code', width: 100, render: (r) => <span className="mono">{r.code}</span> },
    {
      key: 'name',
      header: 'Name',
      width: 230,
      render: (r) => (
        <span className="withicon">
          <AppIcon kind={k.icon} size={14} />
          {r.nameEn}
        </span>
      ),
    },
    { key: 'urdu', header: 'Urdu name', width: 150, render: (r) => (r.nameUr ? <span className="urdu">{r.nameUr}</span> : <span className="dim">—</span>) },
    ...extra,
    { key: 'used', header: k.usedBy, align: 'end', width: 90, render: (r) => r.inUse },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (r) => (r.isActive ? <span className="pill">Active</span> : <span className="pill pill--alert">Switched off</span>),
    },
  ];

  if (form) {
    return (
      <div className="workbench__main workbench__main--form">
        <MasterForm
          key={form.mode === 'edit' ? form.row.id : 'create'}
          kind={kind}
          editing={form.mode === 'edit' ? form.row : undefined}
          onCancel={() => setForm(null)}
          onSaved={(message, id) => {
            setForm(null);
            invalidate();
            if (id) setSelectedKey(id);
            setNotice({ tone: 'ok', message });
          }}
        />
      </div>
    );
  }

  const tabs: DetailTab[] = selected
    ? [
        {
          id: 'details',
          label: 'Details',
          icon: 'details',
          content: (
            <dl className="detaillist">
              <dt>Code</dt>
              <dd className="mono">{selected.code}</dd>
              <dt>Name</dt>
              <dd>
                {selected.nameEn}
                {selected.nameUr && <span className="urdu"> · {selected.nameUr}</span>}
              </dd>
              {kind === 'pay-components' && (
                <>
                  <dt>Kind</dt>
                  <dd>{selected.kind ? PAY_KIND_TEXT[selected.kind] : '—'}</dd>
                  <dt>Worked out by payroll</dt>
                  <dd>{selected.systemCode ? 'Yes — it cannot be removed, only renamed' : 'No — typed in or set on each person’s pay'}</dd>
                </>
              )}
              <dt>In use</dt>
              <dd>{selected.inUse > 0 ? `${selected.inUse} record(s) use it` : 'Not used yet'}</dd>
              <dt>Status</dt>
              <dd>{selected.isActive ? 'Active' : 'Switched off'}</dd>
              <dt>Last changed</dt>
              <dd>{shortDate(selected.updatedAt)}</dd>
            </dl>
          ),
        },
      ]
    : [];

  return (
    <div className="workbench__main">
      <p className="muted">{k.intro}</p>
      <div className="filterbar">
        <label className="settings__toggle">
          <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />
          <span className="field__label">Show switched off</span>
        </label>
      </div>
      <ActionBar actions={toolbar} />
      {list.error && <ErrorNote message={list.error.message} />}
      {notice && (notice.tone === 'ok' ? <OkNote message={notice.message} /> : <ErrorNote message={notice.message} />)}
      <SplitView
        id={`hr-setup-${kind}`}
        direction="column"
        initial={380}
        firstLabel={`${k.many} list`}
        first={
          <DataGrid
            id={`hr-setup-${kind}`}
            label={k.many}
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            selectedKey={selectedKey}
            onSelect={(key) => {
              setSelectedKey(key);
              setNotice(null);
            }}
            onOpen={(r) => {
              setSelectedKey(r.id);
              if (editable) setForm({ mode: 'edit', row: r });
            }}
            onEdit={(r) => {
              if (editable) setForm({ mode: 'edit', row: r });
            }}
            actions={rowActions}
            loading={list.isPending}
            empty={`No ${k.many.toLowerCase()} yet.`}
          />
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab="details"
            onTabChange={() => {}}
            empty={`Choose a ${k.one} to see its details.`}
            title={
              selected && (
                <span>
                  <span className="mono">{selected.code}</span> {selected.nameEn}
                </span>
              )
            }
          />
        }
      />
    </div>
  );
}

function MasterForm({
  kind,
  editing,
  onCancel,
  onSaved,
}: {
  kind: MasterKind;
  editing: MasterRow | undefined;
  onCancel: () => void;
  onSaved: (message: string, id?: string) => void;
}) {
  const k = KINDS[kind];
  const system = kind === 'pay-components' && !!editing?.systemCode;
  const [v, setV] = useState(() => ({
    nameEn: editing?.nameEn ?? '',
    nameUr: editing?.nameUr ?? '',
    startTime: editing?.startTime ?? '08:00',
    endTime: editing?.endTime ?? '17:00',
    isNight: editing?.isNight ?? false,
    payKind: (editing?.kind ?? 'EARNING') as PayKind,
    taxable: editing?.taxable ?? true,
    countsForOvertime: editing?.countsForOvertime ?? false,
    percentOfBasic: editing?.percentOfBasic ? String(Number(editing.percentOfBasic)) : '',
    isPaid: editing?.isPaid ?? true,
    yearlyDays: editing?.yearlyDays ? String(Number(editing.yearlyDays)) : '10',
    carryForwardMax: editing?.carryForwardMax ? String(Number(editing.carryForwardMax)) : '0',
    encashable: editing?.encashable ?? false,
    forDailyWorkers: editing?.forDailyWorkers ?? false,
  }));
  const [problem, setProblem] = useState<string | null>(null);
  const set = <K extends keyof typeof v>(key: K) => (value: (typeof v)[K]) => setV((s) => ({ ...s, [key]: value }));

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { nameEn: v.nameEn.trim(), nameUr: v.nameUr.trim() || null };
      if (kind === 'shifts') Object.assign(body, { startTime: v.startTime, endTime: v.endTime, isNight: v.isNight });
      if (kind === 'pay-components') {
        if (!editing) body.kind = v.payKind;
        if (v.payKind === 'EARNING') Object.assign(body, { taxable: v.taxable, countsForOvertime: v.countsForOvertime });
        body.percentOfBasic = v.percentOfBasic.trim() || null;
      }
      if (kind === 'leave-types') {
        Object.assign(body, {
          isPaid: v.isPaid,
          yearlyDays: v.yearlyDays.trim() || '0',
          carryForwardMax: v.carryForwardMax.trim() || '0',
          encashable: v.encashable,
          forDailyWorkers: v.forDailyWorkers,
        });
      }
      if (editing) {
        await api.patch(`/api/v1/hr/setup/${kind}/${editing.id}`, body);
        return { id: editing.id, code: editing.code };
      }
      return api.post<{ id: string; code: string }>(`/api/v1/hr/setup/${kind}`, body);
    },
    onSuccess: (r) => onSaved(editing ? `${r.code} saved.` : `${r.code} added.`, r.id),
    onError: (e) => setProblem(errorMessage(e, 'Could not save')),
  });

  function submit() {
    if (!v.nameEn.trim()) return setProblem('Enter the name.');
    if (kind === 'shifts' && (!v.startTime || !v.endTime)) return setProblem('Enter the start and end time.');
    setProblem(null);
    save.mutate();
  }

  return (
    <FormPanel
      title={editing ? `Edit ${editing.code} — ${editing.nameEn}` : `Add a ${k.one}`}
      icon={<AppIcon kind={k.icon} />}
      intro={system ? 'Payroll works this pay item out itself. You can change its name; how it is worked out stays the same.' : k.intro}
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <FormGroup title="Name" columns={3}>
          {editing && (
            <Field
              label="Code"
              mono
              value={editing.code}
              onChange={() => {}}
              disabled
              hint="Given when it was created, and never changes"
            />
          )}
          <Field label="Name" required value={v.nameEn} onChange={set('nameEn')} placeholder={k.example} />
          <Field label="Name in Urdu" urdu value={v.nameUr} onChange={set('nameUr')} />
        </FormGroup>

        {kind === 'shifts' && (
          <FormGroup title="Hours" columns={3}>
            <Field label="Starts at" type="time" required value={v.startTime} onChange={set('startTime')} />
            <Field label="Ends at" type="time" required value={v.endTime} onChange={set('endTime')} />
            <Toggle label="Night shift" checked={v.isNight} onChange={set('isNight')} />
          </FormGroup>
        )}

        {kind === 'pay-components' && (
          <FormGroup
            title="How it works"
            columns={2}
            hint={v.payKind === 'EARNING' ? 'Taxable earnings count towards income tax. Those that count for overtime make the overtime rate higher.' : undefined}
          >
            <Select
              label="Kind"
              required
              disabled={!!editing}
              value={v.payKind}
              onChange={(x) => set('payKind')(x as PayKind)}
              options={
                editing?.kind === 'EMPLOYER'
                  ? [{ value: 'EMPLOYER', label: 'Company cost (paid on top, not taken from pay)' }]
                  : [
                      { value: 'EARNING', label: 'Earning (added to pay)' },
                      { value: 'DEDUCTION', label: 'Deduction (taken from pay)' },
                    ]
              }
              placeholder=""
              hint={editing ? 'Cannot be changed once added' : undefined}
            />
            <Field
              label="% of basic pay"
              type="number"
              min={0}
              max={1000}
              value={v.percentOfBasic}
              onChange={set('percentOfBasic')}
              hint="Leave empty for a fixed amount set on each person’s pay."
            />
            {v.payKind === 'EARNING' && (
              <>
                <Toggle label="Taxable" checked={v.taxable} onChange={set('taxable')} />
                <Toggle label="Counts for overtime" checked={v.countsForOvertime} onChange={set('countsForOvertime')} />
              </>
            )}
          </FormGroup>
        )}

        {kind === 'leave-types' && (
          <FormGroup title="Days and rules" columns={2}>
            <Field label="Days a year" type="number" required min={0} max={366} value={v.yearlyDays} onChange={set('yearlyDays')} hint="Whole or half days." />
            <Field
              label="Carry forward up to (days)"
              type="number"
              min={0}
              max={366}
              value={v.carryForwardMax}
              onChange={set('carryForwardMax')}
              hint="Unused days moved to next year, at most this many. 0 means none."
            />
            <Toggle label="Paid leave" checked={v.isPaid} onChange={set('isPaid')} />
            <Toggle label="Paid out on leaving" checked={v.encashable} onChange={set('encashable')} />
            <Toggle label="Daily-wage staff get it too" checked={v.forDailyWorkers} onChange={set('forDailyWorkers')} />
          </FormGroup>
        )}

        {problem && <ErrorNote message={problem} />}
        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : `Add ${k.one}`}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
